import { ChevronDown, MessageSquareWarning } from 'lucide-react';
import { useId, useState } from 'react';

import { messages } from '@/shared/constants/messages';
import { formatDate } from '@/shared/lib/dates';
import { cn } from '@/shared/lib/utils';
import type { TimelineTransitionEntry } from '@/shared/types/domain';

interface ReviewerRequestProps {
  entry: TimelineTransitionEntry;
  /**
   * Só em telas estreitas (decisão 46): ali cada linha do pedido empurra o
   * primeiro campo para fora da dobra. **Começa aberto** — recolher é escolha
   * de quem já leu, nunca o estado inicial de uma instrução.
   */
  collapsible?: boolean;
  className?: string;
}

/**
 * O pedido do revisor, dentro do formulário (decisões 45 e 46).
 *
 * É a instrução vigente da tarefa que a pessoa veio fazer, e por isso ele mora
 * na tela onde ela a executa — não a uma navegação de distância, na linha do
 * tempo de `/projetos/:id`. Sem ele, ajustar um projeto devolvido exigia
 * decorar o apontamento antes de clicar em "Ajustar projeto".
 *
 * **O ÚLTIMO pedido, não o histórico** (`lastChangeRequest`). As rodadas
 * anteriores continuam na linha do tempo, que é onde se reconstitui o percurso
 * (US-24); aqui, dois textos concorreriam pela mesma tarefa.
 *
 * **Tom de atenção**, o mesmo de `changes_requested` em `PROJECT_STATUS_TONES`,
 * para o olho ligar as duas coisas: o crachá que ele viu na tela do projeto e o
 * bloco que ele lê aqui são a mesma situação.
 *
 * **Não é truncado e não é dispensável.** "Marcar como lido" exigiria estado
 * por usuário — tabela nova para resolver nada —, e `line-clamp` esconderia
 * justamente a frase que diz o que fazer. O comentário é obrigatório na origem
 * (`decide_project` recusa `request_changes` sem texto), então ele nunca chega
 * vazio.
 */
export function ReviewerRequest({ entry, collapsible = false, className }: ReviewerRequestProps) {
  const [isOpen, setIsOpen] = useState(true);
  const showBody = !collapsible || isOpen;

  /**
   * O bloco aparece DUAS vezes no DOM — uma na coluna do índice (`hidden
   * lg:block`) e outra no topo do documento (`lg:hidden`), como já acontece com
   * `SectionIndex`/`SectionProgressBar`. Id fixo faria as duas cópias
   * colidirem, e `aria-labelledby` de uma apontaria para o título da outra.
   */
  const titleId = useId();
  const bodyId = useId();

  return (
    <section
      aria-labelledby={titleId}
      className={cn(
        'animate-reveal rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm',
        className,
      )}
    >
      <div className="flex items-start gap-2">
        <MessageSquareWarning aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-amber-900" />
        <div className="min-w-0 flex-1">
          <h2 id={titleId} className="font-medium text-amber-900">
            {messages.form.reviewerRequestTitle}
          </h2>
          <p className="text-xs text-amber-900/80">
            {messages.form.reviewerRequestBy(entry.actor_name, formatDate(entry.created_at))}
          </p>
        </div>

        {collapsible ? (
          <button
            type="button"
            onClick={() => setIsOpen((open) => !open)}
            aria-expanded={isOpen}
            aria-controls={bodyId}
            className="-m-1 shrink-0 rounded p-1 text-amber-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
          >
            <span className="sr-only">
              {isOpen
                ? messages.form.reviewerRequestCollapse
                : messages.form.reviewerRequestExpand}
            </span>
            <ChevronDown
              aria-hidden="true"
              className={cn(
                'size-4 transition-transform duration-base ease-out',
                !isOpen && '-rotate-90',
              )}
            />
          </button>
        ) : null}
      </div>

      {showBody ? (
        <p
          id={bodyId}
          className="mt-2 whitespace-pre-line pl-6 leading-relaxed text-amber-900/90"
        >
          {entry.comment}
        </p>
      ) : null}
    </section>
  );
}
